import { getOrderTypeCardStyle, getOrderTypeLabel, MoldOrder, MoldOrderFirstLevelType, MoldOrderMainType } from '../../types/mold';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMoldOrderDetail } from '../../hooks/useMoldOrder';
import MoldOrderStatus from '../shared/MoldOrderStatus';
import { formatShortThaiDate, getRemainingDaysInThai, getShiftLabel } from '../../utils/dateUtils';
import { Calendar, CalendarClock, Search, ArrowRightLeft } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { getDisplayTodayDate } from '../utils';
import MoldOrderTypeFilter from '../KanbanBoard/MoldOrderTypeFilter';
import { usePreferences } from '../../context/PreferencesContext';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import "../../custom.css";
import { sortSingleMoldOrder } from '../../utils/priority';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getPriorityLabel } from '../MoldDetail/utils';
import { useAppState } from '../../context/AppStateContext';
import { getSubtitleFieldRenderer } from '../MoldOrderList/fieldRenderers';
import React from 'react';

interface MoldOrderPrioritizerProps {
    moldOrders: MoldOrder[];
    currentMoldOrder: MoldOrder | null;
    moldOrderFirstLevelType: MoldOrderFirstLevelType;
}

const PRIORITY_BUCKETS = [
    { id: 'urgent', priority: 1, label: 'เร่งด่วนมาก', color: 'bg-red-50 border-red-500', headerColor: 'bg-red-500 text-white' },
    { id: 'high', priority: 100, label: 'เร่งด่วน', color: 'bg-orange-50 border-orange-500', headerColor: 'bg-orange-500 text-white' },
    { id: 'normal', priority: 200, label: 'ปกติ', color: 'bg-green-50 border-green-500', headerColor: 'bg-green-500 text-white' },
];

const MoldOrderPrioritizer: React.FC<MoldOrderPrioritizerProps> = ({
    moldOrders,
    currentMoldOrder,
    moldOrderFirstLevelType,
}) => {
    const { getBoards, getTaskRowConfig, getMoldOrderCardHeadlines } = useAppState();
    const [orders, setOrders] = useState<MoldOrder[]>(moldOrders);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const { updatePreference } = usePreferences();
    const [selectedOrderType, setSelectedOrderType] = useState<MoldOrderMainType | 'ALL'>('ALL');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const navigate = useNavigate();
    const { updateMoldOrderById } = useMoldOrderDetail();

    // Find the selected group for easy access to label and subTypes
    const selectedGroup = useMemo(() => {
        const board = getBoards().find(board => board.group === moldOrderFirstLevelType);
        const groups = board?.filters.moldOrderTypeGroups || [];
        
        if (selectedOrderType === 'ALL') {
            return { value: 'ALL', label: 'ทั้งหมด', subTypes: [] };
        }
        return groups.find(group => group.value === selectedOrderType) 
            || { value: selectedOrderType, label: selectedOrderType, subTypes: [] };
    }, [selectedOrderType, moldOrderFirstLevelType, getBoards]);

    const filteredOrders = useMemo(() => {
        return orders.filter(order => {
            const matchesSearch = (
                order.id.toString().includes(searchTerm) ||
                order.mold.sbiId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                order.mold.productParts.some(p => p.partNumber.toLowerCase().includes(searchTerm.toLowerCase()))
            );

            const matchesType = selectedOrderType === 'ALL' || selectedGroup.subTypes.includes(order.type);

            let matchesDateRange = true;
            if (order.orderDetail.dueDate && startDate && endDate) {
                const orderDate = new Date(order.orderDetail.dueDate);
                matchesDateRange = (!startDate || orderDate >= startDate) &&
                    (!endDate || orderDate <= endDate);
            }

            return matchesSearch && matchesType && matchesDateRange;
        });
    }, [orders, searchTerm, selectedOrderType, selectedGroup.subTypes, startDate, endDate]);

    const ordersByPriority = useMemo(() => {
        return PRIORITY_BUCKETS.reduce((acc, bucket) => {
            let bucketOrders = filteredOrders.filter(order => order.priority === bucket.priority);

            // Sort orders within each bucket
            acc[bucket.id] = bucketOrders.sort((a, b) => {
                // Current mold order always comes first
                if (a.id === currentMoldOrder?.id) return -1;
                if (b.id === currentMoldOrder?.id) return 1;

                return sortSingleMoldOrder(a, b);
            });

            return acc;
        }, {} as Record<string, MoldOrder[]>);
    }, [filteredOrders, currentMoldOrder]);

    // Add scrolling effect when component mounts or when filters change
    useEffect(() => {
        if (currentMoldOrder) {
            const currentCard = document.getElementById(`mold-order-${currentMoldOrder.id}`);
            if (currentCard) {
                setTimeout(() => {
                    currentCard.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest'
                    });
                }, 100);
            }
        }
    }, [currentMoldOrder, ordersByPriority]);

    const handleDragEnd = async (result: DropResult) => {
        const { destination, source, draggableId } = result;

        if (!destination) return;
        if (destination.droppableId === source.droppableId) return;

        const orderId = parseInt(draggableId);
        const newPriority = PRIORITY_BUCKETS.find(b => b.id === destination.droppableId)?.priority || 200;

        const updatedOrders = orders.map(order =>
            order.id === orderId ? { ...order, priority: newPriority } : order
        );

        setOrders(updatedOrders);
        await updateMoldOrderById(orderId, { priority: newPriority });
    };

    const handleOrderTypeChange = (newType: MoldOrderMainType | 'ALL') => {
        setSelectedOrderType(newType);
        updatePreference({ selectedOrderType: newType });
    };

    const handleClearDates = () => {
        setStartDate(null);
        setEndDate(null);
    };

    const handleQuickPriority = async (orderId: number, newPriority: number) => {
        const updatedOrders = orders.map(o =>
            o.id === orderId ? { ...o, priority: newPriority } : o
        );
        setOrders(updatedOrders);
        await updateMoldOrderById(orderId, { priority: newPriority });

        // Show toast message
        toast.success(`อัปเดตใบงาน ${orderId} เป็น ${getPriorityLabel(newPriority)} เรียบร้อยแล้ว!`);
    };

    const renderMobileCard = (order: MoldOrder) => {
        const dueDate = order.orderDetail?.dueDate ? new Date(order.orderDetail.dueDate) : null;
        const isOverdue = dueDate ? dueDate < new Date() : false;
        
        // Get client-specific configuration for TaskRow info field
        const taskRowConfig = getTaskRowConfig();
        const InfoFieldRenderer = getSubtitleFieldRenderer(taskRowConfig.infoField);

        return (
            <div className="bg-white rounded-lg shadow-sm p-3 relative" id={`mold-order-${order.id}`}>
                {currentMoldOrder?.id === order.id && (
                    <div className="absolute -top-1 -right-1 z-10">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white shadow-sm">
                            ใบงานที่กำลังแก้ไข
                        </span>
                    </div>
                )}

                {/* TaskRow-style content */}
                <div className="flex items-center mb-3">
                    <div className="relative flex items-center justify-center w-9 h-9 rounded-full bg-blue-50 mr-3 flex-shrink-0">
                        <span className="text-sm font-semibold text-blue-600">#{order.id}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-blue-600">{getOrderTypeLabel(order.type)}</span>
                            <span className="text-xs text-gray-900 truncate" title={order.mold.sbiId}>
                                {order.mold.sbiId}
                            </span>
                            <div className="text-xs text-gray-500 truncate">
                                {React.createElement(InfoFieldRenderer, {
                                    moldOrder: order,
                                    value: taskRowConfig.infoField === 'productParts' 
                                        ? order.mold?.productParts?.map((p: any) => p.partNumber).join(', ') || ''
                                        : String(order.mold?.[taskRowConfig.infoField as keyof typeof order.mold] || ''),
                                    fieldName: taskRowConfig.infoField,
                                })}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-col items-end ml-3 min-w-[90px]">
                        <span className="text-xs text-gray-500 mb-1">
                            {dueDate ? dueDate.toLocaleDateString('th-TH', { year: '2-digit', month: '2-digit', day: '2-digit' }) : 'ไม่ระบุวัน'}
                        </span>
                        {isOverdue ? (
                            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-semibold">เลยกำหนด</span>
                        ) : (
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-semibold">ปกติ</span>
                        )}
                    </div>
                </div>

                {/* Priority Actions */}
                <div className="grid grid-cols-3 gap-2 pt-2 border-t">
                    <button
                        onClick={() => handleQuickPriority(order.id, 1)}
                        className={`flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-xs
                            ${order.priority === 1
                                ? 'bg-red-600 text-white'
                                : 'bg-red-50 text-red-600 active:bg-red-100'}`}
                    >
                        เร่งด่วนมาก
                    </button>
                    <button
                        onClick={() => handleQuickPriority(order.id, 100)}
                        className={`flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-xs
                            ${order.priority === 100
                                ? 'bg-orange-600 text-white'
                                : 'bg-orange-50 text-orange-600 active:bg-orange-100'}`}
                    >
                        เร่งด่วน
                    </button>
                    <button
                        onClick={() => handleQuickPriority(order.id, 200)}
                        className={`flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-xs
                            ${order.priority === 200
                                ? 'bg-green-600 text-white'
                                : 'bg-green-50 text-green-600 active:bg-green-100'}`}
                    >
                        ปกติ
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="h-screen flex flex-col bg-gray-50">
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-6xl mx-auto p-2 sm:p-4">
                    {/* Updated header layout */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="flex-1 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl sm:text-2xl font-bold">จัดลำดับใบงาน</h2>
                                <span className="text-sm text-gray-500">#{currentMoldOrder?.id}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2 text-xs sm:text-sm">
                                    <Calendar size={16} className="hidden sm:block" />
                                    <span>{getDisplayTodayDate()}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Priority Summary */}
                    <div className='flex flex-col sm:flex-row items-center gap-4'>
                        <div className="grid grid-cols-3 sm:flex gap-2 sm:gap-4 text-xs sm:text-sm">
                            <div className="bg-red-100 px-2 sm:px-4 py-2 rounded-lg text-center sm:text-left">
                                <span className="font-bold text-red-700">เร่งด่วนมาก: {ordersByPriority.urgent?.length || 0}</span>
                            </div>
                            <div className="bg-orange-100 px-2 sm:px-4 py-2 rounded-lg text-center sm:text-left">
                                <span className="font-bold text-orange-700">เร่งด่วน: {ordersByPriority.high?.length || 0}</span>
                            </div>
                            <div className="bg-green-100 px-2 sm:px-4 py-2 rounded-lg text-center sm:text-left">
                                <span className="font-bold text-green-700">ปกติ: {ordersByPriority.normal?.length || 0}</span>
                            </div>
                        </div>

                        {/* Instructions */}
                        <div className="hidden sm:block p-1 rounded-lg">
                            <div className="flex items-center">
                                <ArrowRightLeft className="text-blue-600 mr-2" />
                                <div>
                                    <p className="text-sm text-blue-700">ลากและวางใบงานเพื่อจัดลำดับ</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add DateRange Filter below the type filter */}
            <div className="bg-white border-b border-gray-200 px-4 py-2">
                <div className="max-w-6xl mx-auto sm:px-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                        {/* Existing type filter */}
                        <div className="grow flex items-center gap-2">
                            <span className="text-sm text-gray-500">ประเภทใบงาน:</span>
                            <MoldOrderTypeFilter
                                onTypeCheck={handleOrderTypeChange}
                                selectedOrderType={selectedOrderType}
                                options={getBoards().find(board => board.group === moldOrderFirstLevelType)?.filters.moldOrderTypeGroups || []}
                            />
                        </div>

                        {/* Date Range Filter */}
                        <div className="grow flex items-center gap-2">
                            <span className="text-sm text-gray-500">กำหนดเสร็จ:</span>
                            <div className="flex items-center gap-2">
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="วันเริ่มต้น"
                                    className="text-sm border rounded-lg px-2 py-1 w-32 z-20"
                                />
                                <span className="text-gray-500">-</span>
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || undefined}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="วันสิ้นสุด"
                                    className="text-sm border rounded-lg px-2 py-1 w-32 z-20"
                                />
                                {(startDate || endDate) && (
                                    <button
                                        onClick={handleClearDates}
                                        className="text-gray-500 hover:text-gray-700"
                                    >
                                        ล้าง
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="grow flex items-center gap-2">
                            {/* Moved search to header right */}
                            <div className="relative w-48">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                    type="text"
                                    placeholder="ค้นหาใบงาน..."
                                    className="w-full pl-8 pr-3 py-1 text-sm border rounded-full"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-hidden">
                <div className="h-full overflow-auto p-4">
                    {/* Desktop View with Drag and Drop */}
                    <div className="hidden sm:block">
                        <DragDropContext onDragEnd={handleDragEnd}>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-full">
                                {PRIORITY_BUCKETS.map(bucket => (
                                    <div key={bucket.id} className={`${bucket.color} border rounded-lg flex flex-col`}>
                                        <div className={`${bucket.headerColor} p-3 flex justify-between items-center sticky top-0 z-10`}>
                                            <h3 className="font-semibold">{bucket.label}</h3>
                                            <span className="bg-white/20 px-2 py-1 rounded-full text-sm">
                                                {ordersByPriority[bucket.id]?.length || 0}
                                            </span>
                                        </div>
                                        <Droppable droppableId={bucket.id}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                    className={`p-2 flex-1 overflow-y-auto min-h-[calc(100vh-400px)] transition-colors ${snapshot.isDraggingOver ? 'bg-gray-100' : ''
                                                        }`}
                                                >
                                                    {ordersByPriority[bucket.id]?.map((order, index) => {
                                                        const headlines = getMoldOrderCardHeadlines(order);
                                                        const dueDate = order.orderDetail?.dueDate ? new Date(order.orderDetail.dueDate) : null;
                                                        const dueDateInfo = dueDate ? getRemainingDaysInThai(dueDate) : null;
                                                        const displayDueDate = dueDate ? formatShortThaiDate(dueDate) : '';
                                                        const displayShiftLabel = order.orderDetail.shiftCompleted ? `- กะ ${getShiftLabel(order.orderDetail.shiftCompleted)}` : '';
                                                        const statusColor = dueDateInfo?.status === 'red' ? 'text-red-600' : dueDateInfo?.status === 'yellow' ? 'text-yellow-600' : 'text-green-600';

                                                        return (
                                                            <Draggable
                                                                key={order.id}
                                                                draggableId={order.id.toString()}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        id={`mold-order-${order.id}`}
                                                                        className={`
                                                                            relative bg-white rounded-lg shadow-sm p-3 mb-2
                                                                            ${snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}
                                                                            ${currentMoldOrder?.id === order.id ? 'ring-2 ring-yellow-400' : ''}
                                                                        `}
                                                                    >
                                                                        {currentMoldOrder?.id === order.id && (
                                                                            <div className="absolute -top-1 -right-1 z-10">
                                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-500 text-white shadow-sm">
                                                                                    ใบงานที่กำลังแก้ไข
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        <div className="mt-2">
                                                                            <div className="flex items-center gap-2 mb-2">
                                                                                <span className="font-semibold">#{order.id}</span>
                                                                                <MoldOrderStatus moldOrder={order} />
                                                                            </div>
                                                                            <div className="text-sm text-gray-600">{headlines.title}</div>
                                                                            <div className="text-xs text-gray-500">{headlines.subtitle}</div>
                                                                            <div className="mt-2 flex items-center justify-between">
                                                                                <span className={`${getOrderTypeCardStyle(order.type)} text-xs py-1 px-2 rounded-lg`}>
                                                                                    {getOrderTypeLabel(order.type)}
                                                                                </span>
                                                                                {dueDate && (
                                                                                    <div className={`flex items-center text-xs gap-1 ${statusColor}`}>
                                                                                        <CalendarClock size={12} />
                                                                                        <span>{displayDueDate} {displayShiftLabel}</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        );
                                                    })}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </div>
                                ))}
                            </div>
                        </DragDropContext>
                    </div>

                    {/* Mobile View */}
                    <div className="sm:hidden">
                        {PRIORITY_BUCKETS.map(bucket => (
                            <div key={bucket.id} className="mb-4">
                                <div className={`${bucket.headerColor} p-3 flex justify-between items-center`}>
                                    <h3 className="font-semibold text-white">{bucket.label}</h3>
                                    <span className="bg-white/20 px-2 py-1 rounded-full text-xs text-white">
                                        {ordersByPriority[bucket.id]?.length || 0}
                                    </span>
                                </div>
                                <div className="p-2 space-y-3 bg-gray-50 border">
                                    {ordersByPriority[bucket.id]?.map((order) => (
                                        <div key={order.id}>
                                            {renderMobileCard(order)}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="bg-white border-t p-2 sm:p-4 sticky bottom-0 z-30">
                <div className="max-w-6xl mx-auto flex justify-end">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        เสร็จสิ้น
                    </button>
                </div>
            </div>
            <ToastContainer />
        </div>
    );
};

export default MoldOrderPrioritizer;